import React, { useState, useEffect } from 'react'
import { func, shape, string } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { preloadTransaction } from '../../redux/booking/actions'
import {
  selectPreloadingTransaction,
  selectPreloadTransactionSuccess,
  selectPreloadTransactionError,
  selectPreloadedTicketNumbers
} from '../../redux/booking/selectors'
import MonerisCheckoutFrameWrapper from './MonerisCheckoutFrameWrapper'
import { preparePreloadData } from './utilities'
import { PAYMENT_METHOD_INVOICE } from './constants'
// hard coded labels temporarily as fallback values
import HARD_CODE_LABELS from '../../configuration/labels'

const MonerisCheckoutContainer = ({
  bookingConfirmationData,
  ferryId,
  labels,
  lang,
  sessionId,
  handleRouteChange,
  openModal,
  cancelTransactionRouteTarget,
  monerisCheckoutWidth,
  paymentContainerRef,
  isTelephoneBooking
}) => {
  const dispatch = useDispatch()

  const preloadingTransaction = useSelector(selectPreloadingTransaction)
  const preloadTransactionError = useSelector(selectPreloadTransactionError)
  const preloadTransactionSuccess = useSelector(selectPreloadTransactionSuccess)
  const preloadedTickets = useSelector(selectPreloadedTicketNumbers)

  const [bookingPreloaded, setBookingPreloaded] = useState(false)
  const [preloadError, setPreloadError] = useState(false)
  const [awaitingPreload, setAwaitingPreload] = useState(true)

  // Note: Preload success reducer replaces all previous tickets with 1 new ticket
  const ticketNumber = preloadedTickets?.ticket
  const orderNo = preloadedTickets?.orderNo

  const pageData =
    !preloadError &&
    preparePreloadData({
      ferryId,
      bookingConfirmationData,
      lang,
      labels,
      sessionId,
      isTelephoneBooking
    })

  const isInvoicePayment =
    bookingConfirmationData.paymentMethod === PAYMENT_METHOD_INVOICE

  // check preload state
  useEffect(() => {
    if (preloadError || preloadingTransaction || awaitingPreload) return

    if (preloadTransactionError && !preloadTransactionSuccess) {
      setPreloadError(true)
    } else {
      setBookingPreloaded(true)
    }
  }, [
    preloadError,
    preloadingTransaction,
    preloadTransactionError,
    preloadTransactionSuccess,
    awaitingPreload
  ])

  // preload transaction data including ticket number, order
  useEffect(() => {
    if (isInvoicePayment) return
    if (
      !preloadError &&
      !preloadingTransaction &&
      !preloadTransactionSuccess &&
      pageData &&
      !bookingPreloaded
    ) {
      if (pageData?.preloadParams) {
        setAwaitingPreload(false)
        dispatch(
          preloadTransaction(
            { ...pageData.preloadParams },
            bookingConfirmationData.bookingNumber
          )
        )
      } else {
        setPreloadError(true)
      }
    }
  }, [
    isInvoicePayment,
    preloadError,
    preloadingTransaction,
    preloadTransactionSuccess,
    pageData,
    dispatch,
    bookingPreloaded,
    bookingConfirmationData.bookingNumber,
    ferryId
  ])

  const ready =
    !pageData ||
    preloadTransactionError ||
    preloadError ||
    (!preloadingTransaction && bookingPreloaded && ticketNumber)

  /*
   * From the checkout docs
   * https://developer.moneris.com/sitecore/media%20library/Hidden/MCO/Client-Side%20Checkout%20Page
   * (Optional): If you are not using the "Full screen" window sizing option, you will need to define the size of your window by creating another <div> around this one, for example:
   *
   * Sorry, we need to dynamically update the width provided to the container around moneris checkout's iFrame.
   * The initial width is set by PaymentContainer as the iFrame will blow out any containers further down.
   * */
  const [width, setWidth] = useState(monerisCheckoutWidth)

  useEffect(() => {
    function updateWidth() {
      const newWidth = paymentContainerRef?.current?.getBoundingClientRect()
        ?.width
      if (newWidth && newWidth - 2 !== width) {
        setWidth(newWidth - 2)
      }
    }

    window.addEventListener('resize', debounce(updateWidth, 150))

    return () =>
      window.removeEventListener('resize', debounce(updateWidth, 150))
  }, [width, paymentContainerRef])

  // check payment method, if its invoice booking, display message indicating payment not allowed
  if (isInvoicePayment) {
    return (
      <p className="u-page-view-container u-padding-top u-text-center u-error-color">
        {labels.invoicePaymentErrorMsg ||
          HARD_CODE_LABELS[lang].invoicePaymentErrorMsg}
      </p>
    )
  }

  // FIXME: page data error
  if (!pageData) {
    return (
      <p className="u-page-view-container u-padding-top u-text-center u-error-color">
        {labels.pageDataInvalidErroMsg ||
          HARD_CODE_LABELS[lang].pageDataInvalidErroMsg}
      </p>
    )
  }

  return (
    <MonerisCheckoutFrameWrapper
      preloadReady={!!ready}
      ticketNumber={ticketNumber}
      orderNo={orderNo}
      preloadError={preloadError || !pageData}
      pageData={pageData}
      labels={labels}
      handleRouteChange={handleRouteChange}
      openModal={openModal}
      cancelTransactionRouteTarget={cancelTransactionRouteTarget}
      width={width}
      isTelephoneBooking={isTelephoneBooking}
    />
  )
}

MonerisCheckoutContainer.propTypes = {
  bookingConfirmationData: shape().isRequired,
  ferryId: string.isRequired,
  labels: shape().isRequired,
  lang: string.isRequired,
  sessionId: string.isRequired,
  handleRouteChange: func.isRequired,
  openModal: func.isRequired,
  modalIsOpen: shape().isRequired,
  cancelTransactionRouteTarget: string
}

export default MonerisCheckoutContainer
